import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, can, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EstadoBadge, OrigenBadge, Spinner, Empty } from "@/components/common";
import { Search, Plus, Upload, Download, Users, FileDown, X, Eye, Trash2, RefreshCw, CopyCheck } from "lucide-react";
import { EmpresaUsuariaSelect, EmpresaBadge } from "@/components/EmpresaUsuariaSelect";
import { AccesoBadge, GestionarAccesoBtn } from "@/components/GestionarAcceso";

const TABS = [
  { key: "", label: "Todos" },
  { key: "roge", label: "Ingresados por Roge" },
  { key: "carga_inicial", label: "Carga inicial" },
];

function Field({ k, label, type = "text", required, value, onChange }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-500">{label}{required && " *"}</label>
      <input
        data-testid={`nuevo-${k}`}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20"
      />
    </div>
  );
}

function PersonaExistente({ d, onAbrir, onAgregar, saving }) {
  return (
    <div className="col-span-full space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="persona-existente-alerta">
      <p className="font-semibold">{d.msg}</p>
      <p>{d.persona.nombre_completo} · {d.persona.cedula}</p>
      {d.nombre_distinto && <p className="text-xs" data-testid="persona-nombre-distinto">El nombre escrito no coincide con el registrado. Si continúas, quedará una alerta para que el administrador lo revise.</p>}
      <ul className="text-xs">{d.vinculaciones.map((v) => <li key={v.id}>• {v.empresa_cliente} · {v.cargo || "sin cargo"} · ingreso {v.fecha_ingreso || "—"} · {v.estado}</li>)}</ul>
      <div className="flex flex-wrap gap-2 pt-1">
        {d.vinculaciones[0] && <button type="button" onClick={() => onAbrir(d.vinculaciones[0].id)} data-testid="persona-abrir-ficha" className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold">Abrir ficha existente</button>}
        {d.code === "persona_existente" && <button type="button" disabled={saving} onClick={onAgregar} data-testid="persona-agregar-vinculacion" className="rounded-lg bg-vino-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">Agregar nueva vinculación a esta persona</button>}
      </div>
    </div>
  );
}

function NuevoRegistroModal({ onClose, onSaved, onAbrir }) {
  const [form, setForm] = useState({
    nombre_completo: "", cedula: "", empresa_cliente: "", cargo: "",
    proceso: "", fecha_ingreso: "", correo_electronico: "", telefono: "",
  });
  const [saving, setSaving] = useState(false);
  const [existente, setExistente] = useState(null);
  const set = (k, v) => { setExistente(null); setForm((f) => ({ ...f, [k]: v })); };

  const enviar = async (confirmar) => {
    setSaving(true);
    try {
      const { data } = await api.post("/vinculaciones", { ...form, confirmar_persona: confirmar });
      toast.success(confirmar ? "Nueva vinculación agregada a la persona existente" : "Registro creado (origen Roge)");
      onSaved(data.id);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code) setExistente(d);
      else toast.error(formatApiErrorDetail(d));
    } finally {
      setSaving(false);
    }
  };
  const submit = (e) => { e.preventDefault(); if (!form.empresa_cliente) { toast.error("Selecciona la empresa usuaria o Trabajador de planta"); return; } enviar(false); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg animate-fade-in-up rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-slate-900">Nuevo registro (Roge)</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="cerrar-modal">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-4 text-xs text-slate-500">La persona crea su perfil y HUMAN valida la contratación posteriormente.</p>
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field k="nombre_completo" label="Nombre completo" required value={form.nombre_completo} onChange={(v) => set("nombre_completo", v)} />
          <Field k="cedula" label="Cédula / documento" required value={form.cedula} onChange={(v) => set("cedula", v)} />
          <div><label className="mb-1 block text-xs font-medium text-slate-500">Empresa usuaria *</label><EmpresaUsuariaSelect value={form.empresa_cliente} onChange={(v) => set("empresa_cliente", v)} testid="nuevo-empresa_cliente" /></div>
          <Field k="cargo" label="Cargo" value={form.cargo} onChange={(v) => set("cargo", v)} />
          <Field k="proceso" label="Proceso" value={form.proceso} onChange={(v) => set("proceso", v)} />
          <Field k="fecha_ingreso" label="Fecha de ingreso" type="date" value={form.fecha_ingreso} onChange={(v) => set("fecha_ingreso", v)} />
          <Field k="correo_electronico" label="Correo electrónico" type="email" value={form.correo_electronico} onChange={(v) => set("correo_electronico", v)} />
          <Field k="telefono" label="Teléfono" value={form.telefono} onChange={(v) => set("telefono", v)} />
          {existente && <PersonaExistente d={existente} saving={saving} onAbrir={onAbrir} onAgregar={() => enviar(true)} />}
          <div className="col-span-full mt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancelar
            </button>
            <button data-testid="guardar-registro" type="submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
              {saving && <Spinner />} Guardar registro
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Empleados() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [accesos, setAccesos] = useState(null);
  const gestionaAccesos = can(user, "usuarios");
  useEffect(() => {
    if (gestionaAccesos) api.get("/accesos/estado").then((r) => setAccesos(r.data)).catch(() => setAccesos(null));
  }, [gestionaAccesos]);
  const [empresas, setEmpresas] = useState([]);
  const [search, setSearch] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [estado, setEstado] = useState("");
  const [origen, setOrigen] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [dups, setDups] = useState(0);
  useEffect(() => {
    if (can(user, "editar")) api.get("/duplicados/resumen").then((r) => setDups(r.data.total)).catch(() => {});
  }, [user]);

  const load = () => {
    setRows(null);
    const params = { search, empresa, estado, origen, agrupar: true };
    api.get("/vinculaciones", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, empresa, estado, origen]);

  useEffect(() => {
    api.get("/empresas").then((r) => setEmpresas(r.data)).catch(() => {});
  }, []);

  const [conciliando, setConciliando] = useState(false);
  const [conflictos, setConflictos] = useState(null);
  const conciliar = async () => {
    setConciliando(true);
    try {
      const { data } = await api.post("/admin/conciliar-datos");
      toast.success(`Conciliación lista: ${data.campos_directorio} dato(s) llevados a la matriz y ${data.campos_hoja_vida} a hojas de vida. ${data.conflictos.length} diferencia(s) sin sobrescribir.`);
      if (data.conflictos.length) setConflictos(data.conflictos);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setConciliando(false); }
  };
  const exportFiltered = async () => {
    try {
      await downloadFile(
        `/export/filtered?search=${encodeURIComponent(search)}&empresa=${encodeURIComponent(empresa)}&estado=${estado}&origen=${origen}`,
        "matriz_filtrada.xlsx"
      );
      toast.success("Exportación generada");
    } catch { toast.error("No fue posible exportar"); }
  };
  const exportAll = async () => {
    try { await downloadFile("/export/all", "matriz_completa.xlsx"); toast.success("Exportación completa generada"); }
    catch { toast.error("No fue posible exportar"); }
  };
  const exportTemplate = async () => {
    try { await downloadFile("/export/template", "plantilla_matriz.xlsx"); toast.success("Plantilla descargada"); }
    catch { toast.error("No fue posible descargar la plantilla"); }
  };
  const exportOne = async (id) => {
    try { await downloadFile(`/export/vinculacion/${id}`, "matriz_empleado.xlsx"); toast.success("Empleado exportado"); }
    catch { toast.error("No fue posible exportar"); }
  };

  const removeOne = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar la vinculación de ${nombre || "este empleado"}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/vinculaciones/${id}`);
      toast.success("Vinculación eliminada");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const selectCls = "rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20";

  return (
    <div className="space-y-5">
      {conflictos && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConflictos(null)} />
          <div className="relative z-10 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" data-testid="conciliacion-conflictos">
            <div className="mb-3 flex items-center justify-between"><h3 className="font-display text-lg font-semibold">Diferencias sin sobrescribir</h3><button onClick={() => setConflictos(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
            <p className="mb-3 text-sm text-slate-500">La matriz conserva el dato registrado por HUMAN. Si el de la hoja de vida es el correcto, corrígelo en la ficha del empleado.</p>
            <table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase text-slate-400"><th className="py-1">Empleado</th><th>Campo</th><th>Matriz (HUMAN)</th><th>Hoja de vida</th></tr></thead>
              <tbody>{conflictos.map((c, i) => <tr key={i} className="border-t border-slate-100"><td className="py-1.5">{c.empleado}</td><td>{c.campo.replace(/_/g, " ")}</td><td>{c.antes}</td><td className="font-medium">{c.despues}</td></tr>)}</tbody></table>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Empleados</h1>
          <p className="mt-1 text-sm text-slate-500">Una ficha por persona; sus vinculaciones y contratos se ven al abrirla.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {can(user, "editar") && (
            <button data-testid="btn-revisar-duplicados" onClick={() => navigate("/empleados/duplicados")} className={`flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-semibold ${dups ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border-slate-200 bg-white text-vino-700 hover:bg-vino-50"}`}>
              <CopyCheck className="h-4 w-4" /> Revisar duplicados{dups > 0 && <span className="rounded-full bg-amber-500 px-1.5 text-xs text-white" data-testid="duplicados-count">{dups}</span>}
            </button>
          )}
          {can(user, "crear") && (
            <button data-testid="btn-nuevo-registro" onClick={() => setShowModal(true)} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-vino-700 hover:bg-vino-50">
              <Plus className="h-4 w-4" /> Nuevo registro
            </button>
          )}
          {can(user, "importar") && (
            <button data-testid="btn-ir-importar" onClick={() => navigate("/importaciones")} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-vino-700 hover:bg-vino-50">
              <Upload className="h-4 w-4" /> Importar vigentes
            </button>
          )}
          {can(user, "editar") && (
            <button data-testid="btn-conciliar-datos" onClick={conciliar} disabled={conciliando} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-vino-700 hover:bg-vino-50 disabled:opacity-60">
              <RefreshCw className={`h-4 w-4 ${conciliando ? "animate-spin" : ""}`} /> Conciliar con hojas de vida
            </button>
          )}
          {can(user, "exportar") && (
            <button data-testid="btn-exportar-filtrado" onClick={exportFiltered} className="flex items-center gap-2 rounded-lg bg-vino-700 px-3.5 py-2 text-sm font-semibold text-white hover:bg-vino-800">
              <Download className="h-4 w-4" /> Descargar Excel
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="relative md:col-span-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="input-buscar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o documento"
            className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20"
          />
        </div>
        <select data-testid="filtro-empresa" value={empresa} onChange={(e) => setEmpresa(e.target.value)} className={selectCls}>
          <option value="">Empresa: Todas</option>
          {empresas.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select data-testid="filtro-estado" value={estado} onChange={(e) => setEstado(e.target.value)} className={selectCls}>
          <option value="">Estado: Todos</option>
          <option value="activo">Activo</option>
          <option value="retirado">Retirado</option>
        </select>
        <select data-testid="filtro-origen" value={origen} onChange={(e) => setOrigen(e.target.value)} className={selectCls}>
          <option value="">Origen: Todos</option>
          <option value="roge">Roge</option>
          <option value="carga_inicial">Carga inicial</option>
        </select>
      </div>

      {/* Tabs origen */}
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key || "todos"}`}
            onClick={() => setOrigen(t.key)}
            className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors ${
              origen === t.key ? "bg-vino-700 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
        {can(user, "exportar") && (
          <div className="ml-auto flex gap-2">
            <button data-testid="btn-plantilla" onClick={exportTemplate} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
              <FileDown className="h-3.5 w-3.5" /> Plantilla
            </button>
            <button data-testid="btn-exportar-todo" onClick={exportAll} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
              <FileDown className="h-3.5 w-3.5" /> Listado completo
            </button>
          </div>
        )}
      </div>

      {/* Tabla */}
      {rows === null ? (
        <div className="flex h-48 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>
      ) : rows.length === 0 ? (
        <Empty icon={Users} title="Sin vinculaciones" subtitle="Aún no hay empleados que coincidan. Crea un registro o importa la matriz de empleados vigentes." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" data-testid="tabla-empleados">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Empleado</th>
                  <th className="px-4 py-3 font-semibold">Empresa cliente</th>
                  <th className="px-4 py-3 font-semibold">Cargo</th>
                  <th className="px-4 py-3 font-semibold">Fecha ingreso</th>
                  <th className="px-4 py-3 font-semibold">Origen</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  {accesos && <th className="px-4 py-3 font-semibold">Acceso</th>}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-vino-50/40" data-testid={`fila-${r.id}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => navigate(`/empleados/${r.id}`)} className="text-left">
                        <p className="font-semibold text-slate-800 hover:text-vino-700">{r.nombre_completo || "(sin nombre)"}</p>
                        <p className="text-xs text-slate-400">{r.cedula}</p>
                        {r.vinculaciones_total > 1 && <p className="mt-0.5 text-[11px] font-medium text-vino-600" data-testid={`vinc-total-${r.id}`}>{r.vinculaciones_total} vinculaciones · {r.empresas.join(", ")}</p>}
                      </button>
                    </td>
                    <td className="px-4 py-3"><EmpresaBadge r={r} /></td>
                    <td className="px-4 py-3 text-slate-600">{r.cargo || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.fecha_ingreso || "—"}</td>
                    <td className="px-4 py-3"><OrigenBadge origen={r.origen} /></td>
                    <td className="px-4 py-3"><EstadoBadge estado={r.estado} /></td>
                    {accesos && <td className="px-4 py-3"><AccesoBadge estado={accesos[r.id]} testid={`acceso-${r.id}`} /></td>}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {accesos && <GestionarAccesoBtn vid={r.id} compact onChange={(e) => setAccesos((a) => ({ ...a, [r.id]: e }))} />}
                        <button title="Ver expediente" onClick={() => navigate(`/empleados/${r.id}`)} data-testid={`ver-${r.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-vino-700">
                          <Eye className="h-4 w-4" />
                        </button>
                        {can(user, "exportar") && (
                          <button title="Exportar" onClick={() => exportOne(r.id)} data-testid={`exportar-${r.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-vino-700">
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        {can(user, "eliminar") && (
                          <button title="Eliminar" onClick={() => removeOne(r.id, r.nombre_completo)} data-testid={`eliminar-${r.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-400">
            <span data-testid="conteo-personas">Mostrando {rows.length} {rows.length === 1 ? "persona" : "personas"} · {rows.reduce((s, r) => s + (r.vinculaciones_total || 1), 0)} vinculaciones</span>
            <span>Cada persona conserva su historial de vinculaciones</span>
          </div>
        </div>
      )}

      {showModal && <NuevoRegistroModal onClose={() => setShowModal(false)} onAbrir={(id) => navigate(`/empleados/${id}`)} onSaved={(id) => { setShowModal(false); load(); navigate(`/empleados/${id}`); }} />}
    </div>
  );
}
